import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Receipt, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FacturarePage = () => {
  const { t } = useLanguage();
  const [settings, setSettings] = useState({ fgo_api_key: '', fgo_cif: '', fgo_series: 'FGO' });
  const [savingSettings, setSavingSettings] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [missingCount, setMissingCount] = useState(0);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillConfirmOpen, setBackfillConfirmOpen] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    try {
      const [settingsRes, invoicesRes, missingRes] = await Promise.all([
        axios.get(`${API}/admin/fgo-settings`),
        axios.get(`${API}/admin/invoices`),
        axios.get(`${API}/admin/invoices/missing-count`),
      ]);
      setSettings(settingsRes.data);
      setInvoices(invoicesRes.data);
      setMissingCount(missingRes.data.count);
    } catch (error) {
      console.error('Error loading invoicing data:', error);
      toast.error('Error loading invoicing data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await axios.put(`${API}/admin/fgo-settings`, settings);
      toast.success(t('save') + ' ✓');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleBackfill = async () => {
    setBackfillConfirmOpen(false);
    setBackfilling(true);
    try {
      const res = await axios.post(`${API}/admin/invoices/backfill`);
      const { processed, sent, failed } = res.data;
      toast.success(`${t('invoice_backfill_result')
        .replace('{processed}', processed).replace('{sent}', sent).replace('{failed}', failed)}`);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error sending invoices');
    } finally {
      setBackfilling(false);
    }
  };

  const handleRetry = async (invoiceId) => {
    if (retryingId) return;
    setRetryingId(invoiceId);
    try {
      await axios.post(`${API}/admin/invoices/${invoiceId}/retry`);
      toast.success(t('invoice_retried'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error retrying invoice');
    } finally {
      setRetryingId(null);
      fetchAll();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="facturare-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="facturare-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">{t('facturare')}</h1>
      </div>

      <Card>
        <CardContent className="pt-6">
          <h2 className="text-lg font-semibold mb-4">{t('fgo_settings')}</h2>
          <form onSubmit={handleSaveSettings} className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="fgo-api-key">{t('fgo_api_key')} *</Label>
              <Input
                id="fgo-api-key" type="password" required
                value={settings.fgo_api_key}
                onChange={(e) => setSettings({ ...settings, fgo_api_key: e.target.value })}
                data-testid="fgo-api-key-input"
              />
              <p className="text-xs text-muted-foreground">{t('fgo_api_key_hint')}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fgo-cif">{t('fgo_cif')} *</Label>
                <Input
                  id="fgo-cif" required
                  value={settings.fgo_cif}
                  onChange={(e) => setSettings({ ...settings, fgo_cif: e.target.value })}
                  data-testid="fgo-cif-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fgo-series">{t('fgo_series')}</Label>
                <Input
                  id="fgo-series"
                  value={settings.fgo_series}
                  onChange={(e) => setSettings({ ...settings, fgo_series: e.target.value })}
                  data-testid="fgo-series-input"
                />
              </div>
            </div>
            <div>
              <Button type="submit" disabled={savingSettings} data-testid="save-fgo-settings-btn">
                {t('save_fgo_settings')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {missingCount > 0 && (
        <Card className="border-amber-500">
          <CardContent className="pt-6 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <span>{missingCount} {t('invoices_missing')}</span>
            </div>
            <Button onClick={() => setBackfillConfirmOpen(true)} disabled={backfilling} data-testid="backfill-invoices-btn">
              {t('send_missing_invoices')}
            </Button>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={backfillConfirmOpen} onOpenChange={setBackfillConfirmOpen}>
        <AlertDialogContent data-testid="backfill-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('send_missing_invoices')}</AlertDialogTitle>
            <AlertDialogDescription>
              {missingCount} {t('invoices_missing')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleBackfill} data-testid="confirm-backfill-btn">
              {t('send_missing_invoices')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card>
        <CardContent className="p-0">
          {invoices.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('email')}</TableHead>
                  <TableHead>Nume</TableHead>
                  <TableHead>Sumă</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Nr. FGO</TableHead>
                  <TableHead className="text-right">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                    <TableCell>{invoice.user_email}</TableCell>
                    <TableCell>{invoice.user_name}</TableCell>
                    <TableCell>{invoice.amount} {invoice.currency}</TableCell>
                    <TableCell>
                      <Badge variant={invoice.status === 'sent' ? 'default' : invoice.status === 'failed' ? 'destructive' : 'secondary'}>
                        {invoice.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{invoice.fgo_number ? `${invoice.fgo_series} ${invoice.fgo_number}` : (invoice.error || '-')}</TableCell>
                    <TableCell className="text-right">
                      {(invoice.status === 'failed' || invoice.status === 'processing') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRetry(invoice.id)}
                          disabled={retryingId === invoice.id}
                          data-testid={`retry-invoice-${invoice.id}`}
                        >
                          {t('retry')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Receipt className="w-12 h-12 mb-4 opacity-50" />
              <p>{t('no_invoices')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FacturarePage;
